import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";
import { useState } from "react";

export default function useChargesAndVehicles() {
  const { api, AdminId } = useGlobalContext();
  const [getPackerSlotDetailsLoading, setGetPackerSlotDetailsLoading] =
    useState(false);
const [generatePackerSlotLoading, setGeneratePackerSlotLoading] =
  useState(false);

const [updatePackerSlotsLoading, setUpdatePackerSlotsLoading] =
  useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const getAdminId = () => {
    if (AdminId) return String(AdminId);

    try {
      const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
      return (
        userData?.adminMeta?.adminId ||
        userData?.adminId ||
        userData?.userid ||
        ""
      );
    } catch {
      return "";
    }
  };

  // --------- 1) FETCH VEHICLES ----------
  const fetchVehicleDetailsApi = async (
    setVehicles,
    setLoading,
    silent = false,
  ) => {
    if (!silent) setLoading(true);

    try {
      const payload = {
        userId: String(getAdminId()),
      };

      const res = await api.post(
        "/api/ClientAdmin/v1.0/getAllVehicleDetails",
        payload,
      );

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setVehicles(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to fetch vehicles");
      }

      setVehicles([]);
      return [];
    } catch (error) {
      console.error("fetchVehicleDetailsApi error:", error);
      toast.error("Error fetching vehicles");
      setVehicles([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // --------- 2) FETCH DISTANCE FARES ----------
  const fetchDistanceFaresApi = async (
    setFares,
    setLoading,
    silent = false,
  ) => {
    if (!silent) setLoading(true);

    try {
      const payload = {
        userId: String(getAdminId()),
      };

      const res = await api.post(
        "/api/ClientAdmin/v1.0/getAllDistanceFares",
        payload,
      );

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setFares(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch distance fares",
        );
      }

      setFares([]);
      return [];
    } catch (error) {
      console.error("fetchDistanceFaresApi error:", error);
      toast.error("Error fetching distance fares");
      setFares([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // --------- 3) CREATE VEHICLE ----------
  const createVehicleApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post(
        "/api/ClientAdmin/v2.0/insertVehicleDetails",
        body,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        },
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Vehicle added");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to add vehicle");
      }

      return false;
    } catch (error) {
      console.error("createVehicleApi error:", error);
      toast.error("Error adding vehicle");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 4) DELETE VEHICLE ----------
  const deleteVehicleApi = async (vehicleId, setLoading, onSuccess) => {
    if (!vehicleId) {
      toast.error("Invalid vehicle ID");
      return false;
    }

    setLoading(true);

    try {
      const res = await api.post(
        "/api/ClientAdmin/v2.0/deleteVehicleDetails",
        {
          vehicleId: String(vehicleId),
        },
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        },
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Vehicle deleted");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to delete vehicle");
      }

      return false;
    } catch (error) {
      console.error("deleteVehicleApi error:", error);
      toast.error("Error deleting vehicle");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 5) INSERT / UPDATE DISTANCE FARE ----------
  const saveDistanceFareApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post(
        "/api/ClientAdmin/v1.0/insertDistanceFares",
        body,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        },
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Distance fare saved");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to save fare");
      }

      return false;
    } catch (error) {
      console.error("saveDistanceFareApi error:", error);
      toast.error("Error saving fare");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 6) DELETE DISTANCE FARE ----------
  const deleteDistanceFareApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post(
        "/api/ClientAdmin/v1.0/deleteDistanceFares",
        body,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        },
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Distance fare deleted");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to delete distance fare",
        );
      }

      return false;
    } catch (error) {
      console.error("deleteDistanceFareApi error:", error);
      toast.error("Error deleting distance fare");
      return false;
    } finally {
      setLoading(false);
    }
  };

const getPackerSlotDetailsApi = async ({ userId }) => {
  try {
    setGetPackerSlotDetailsLoading(true);

    const payload = {
      userId: userId || String(getAdminId()),
    };

    const response = await api.post(
      "/api/ClientAdmin/v1.0/getPackerSlotDetails",
      payload,
      {
        headers: {
          accept: "*/*",
          "Content-Type": "application/json-patch+json",
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Get Packer Slot Details Error:", error);

    toast.error(
      error?.response?.data?.responseMessage || "Something went wrong",
    );

    return null;
  } finally {
    setGetPackerSlotDetailsLoading(false);
  }
};

// --------- 8) GENERATE PACKER SLOT ----------
const generatePackerSlotApi = async (body, setLoading, onSuccess) => {
  if (typeof setLoading === "function") {
    setLoading(true);
  }

  setGeneratePackerSlotLoading(true);

  try {
    const payload = {
      adminId: body?.adminId || String(getAdminId()),
      starttime: body?.starttime || "",
      days: Number(body?.days || 0),
    };

    const res = await api.post(
      "/api/ClientAdmin/v1.0/generatePackerSlot",
      payload,
      {
        headers: {
          accept: "*/*",
          "Content-Type": "application/json-patch+json",
        },
      },
    );

    if (res?.data?.responseCode === "00") {
      toast.success(res?.data?.responseMessage || "Packer slots generated");

      if (typeof onSuccess === "function") {
        await onSuccess();
      }

      return true;
    }

    if (shouldShowLocalError(res?.data)) {
      toast.error(
        res?.data?.responseMessage || "Failed to generate packer slots",
      );
    }

    return false;
  } catch (error) {
    console.error("generatePackerSlotApi error:", error);
    toast.error("Error generating packer slots");
    return false;
  } finally {
    setGeneratePackerSlotLoading(false);

    if (typeof setLoading === "function") {
      setLoading(false);
    }
  }
};

// --------- 9) UPDATE PACKER SLOTS ----------
const updatePackerSlotsApi = async (body, setLoading, onSuccess) => {
  if (typeof setLoading === "function") {
    setLoading(true);
  }

  setUpdatePackerSlotsLoading(true);

  try {
    const payload = {
      adminId: body?.adminId || String(getAdminId()),
      date: body?.date || "",
      slot: Array.isArray(body?.slot) ? body.slot.map(String) : [],
      status: body?.status || "",
    };

    const res = await api.post(
      "/api/ClientAdmin/v1.0/updatePackerSlots",
      payload,
      {
        headers: {
          accept: "*/*",
          "Content-Type": "application/json-patch+json",
        },
      },
    );

    if (res?.data?.responseCode === "00") {
      toast.success(res?.data?.responseMessage || "Packer slots updated");

      if (typeof onSuccess === "function") {
        await onSuccess();
      }

      return true;
    }

    if (shouldShowLocalError(res?.data)) {
      toast.error(
        res?.data?.responseMessage || "Failed to update packer slots",
      );
    }

    return false;
  } catch (error) {
    console.error("updatePackerSlotsApi error:", error);
    toast.error("Error updating packer slots");
    return false;
  } finally {
    setUpdatePackerSlotsLoading(false);

    if (typeof setLoading === "function") {
      setLoading(false);
    }
  }
};

return {
  getPackerSlotDetailsLoading,
  generatePackerSlotLoading,
  updatePackerSlotsLoading,

  fetchVehicleDetailsApi,
  fetchDistanceFaresApi,
  createVehicleApi,
  deleteVehicleApi,
  saveDistanceFareApi,
  deleteDistanceFareApi,

  getPackerSlotDetailsApi,
  generatePackerSlotApi,
  updatePackerSlotsApi,
};
}
